import { useFormContext } from 'react-hook-form';

import * as yup from 'yup';

import {
  Form,
  FormCheckboxField,
  FormTextField,
  FormValues,
} from '@sbv-clients/ui-forms';
import { Button, GridLegacy } from '@sbv-clients/ui-shared';

import TimezoneSelectField from './timezone-select-field';

const schema = yup.object({
  name: yup.string().required().label('Facility Name').default(''),
  desktopUserName: yup.string().label('Desktop User Name').default(''),
  databaseName: yup.string().required().label('Database name').default(''),
  databaseHost: yup.string().required().label('Database host').default(''),
  databaseUser: yup.string().required().label('Database username').default(''),
  databasePassword: yup
    .string()
    .required()
    .label('Database password')
    .default(''),
  databaseEncrypt: yup.boolean().required().label('Encrypt').default(false),
  licenseKey: yup.string().required().label('License Key').default(''),
  teamSupportID: yup.string().label('TeamSupportID').default(''),
  timezone: yup.string().required().label('Timezone').default(''),

  usingMobile: yup.boolean().label('Using Mobile').required().default(false),
  usingSbvPro: yup.boolean().label('Using SBV Pro').required().default(false),
  usingSaaSAuthentication: yup
    .boolean()
    .label('Using SaaS Authentication')
    .required()
    .default(false),
});

export type FacilityFormValues = FormValues<typeof schema>;

interface FormBodyProps {
  mode: 'create' | 'update';
  actionTitle: string;
}

const FormBody = ({ mode, actionTitle }: FormBodyProps) => {
  const { formState } = useFormContext();
  const { isSubmitting } = formState;
  return (
    <GridLegacy container spacing={6}>
      <GridLegacy item xs={12}>
        <FormTextField
          fullWidth
          name="name"
          type="text"
          label="Facility Name"
          disabled
        />
      </GridLegacy>
      {mode === 'create' && (
        <GridLegacy item xs={12}>
          <FormTextField
            fullWidth
            name="desktopUserName"
            type="text"
            label="Desktop User Name"
          />
        </GridLegacy>
      )}
      <GridLegacy item xs={12}>
        <FormTextField
          fullWidth
          name="databaseName"
          type="text"
          label="Database name"
        />
      </GridLegacy>
      <GridLegacy item xs={12}>
        <FormTextField
          fullWidth
          name="databaseHost"
          type="text"
          label="Database host"
        />
      </GridLegacy>
      <GridLegacy item xs={12}>
        <FormTextField
          fullWidth
          name="databaseUser"
          type="text"
          label="Database username"
        />
      </GridLegacy>
      <GridLegacy item xs={12}>
        <FormTextField
          fullWidth
          name="databasePassword"
          type="password"
          label="Database password"
        />
      </GridLegacy>
      <GridLegacy item xs={12}>
        <FormTextField
          fullWidth
          name="licenseKey"
          type="text"
          label="License Key"
        />
      </GridLegacy>
      <GridLegacy item xs={12}>
        <FormCheckboxField
          withForm
          fullWidth
          name="databaseEncrypt"
          label="Database Encrypt"
        />
      </GridLegacy>
      <GridLegacy item xs={12}>
        <FormTextField
          fullWidth
          name="teamSupportID"
          type="text"
          label="TeamSupportID"
        />
      </GridLegacy>
      <GridLegacy item xs={12}>
        <TimezoneSelectField />
      </GridLegacy>
      <GridLegacy item xs={12}>
        <FormCheckboxField
          withForm
          fullWidth
          name="usingMobile"
          label="Using Mobile"
        />
      </GridLegacy>
      <GridLegacy item xs={12}>
        <FormCheckboxField
          withForm
          fullWidth
          name="usingSbvPro"
          label="Using SBV Pro"
        />
      </GridLegacy>
      <GridLegacy item xs={12}>
        <FormCheckboxField
          withForm
          fullWidth
          name="usingSaaSAuthentication"
          label="Using SaaS Authentication"
        />
      </GridLegacy>
      <GridLegacy item xs={12}>
        <Button
          fullWidth
          type="submit"
          variant="contained"
          disabled={isSubmitting}
        >
          {actionTitle}
        </Button>
      </GridLegacy>
    </GridLegacy>
  );
};

interface FacilityFormProps extends FormBodyProps {
  defaultValues?: FormValues<typeof schema>;
  onSubmit: (formValues: FacilityFormValues) => Awaited<void>;
}

const FacilityForm = ({
  mode,
  actionTitle,
  defaultValues = schema.getDefault(),
  onSubmit,
}: FacilityFormProps) => {
  return (
    <Form schema={schema} defaultValues={defaultValues} onSubmit={onSubmit}>
      <FormBody mode={mode} actionTitle={actionTitle} />
    </Form>
  );
};

export default FacilityForm;
